import React from 'react';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';

const DIFFICULTY_COLORS = {
  beginner: 'success',
  intermediate: 'warning',
  advanced: 'warning',
  expert: 'error',
};

const CATEGORY_LABELS = {
  mood_disorders: 'Mood',
  anxiety_disorders: 'Anxiety',
  psychotic_disorders: 'Psychosis',
  personality_complex: 'Personality',
  substance_use: 'Substance Use',
  specialist: 'Specialist',
  cross_cutting: 'Cross-Cutting',
};

const ScenarioCard = ({ scenario, onSelect, onEdit, onCopy, onToggleAccess, platformGroupId }) => {
  const {
    _id,
    name,
    description,
    category,
    difficulty,
    scenarioContext,
    learningObjectives = [],
    availableAssessments = [],
    visibilityLevel = 'full_brief',
    referralSummary,
    institutionGroupId,
  } = scenario;

  const isBlind = visibilityLevel === 'blind';
  const isReferralOnly = visibilityLevel === 'referral_only';
  const isPlatformLibrary = !!platformGroupId
    && institutionGroupId
    && (institutionGroupId === platformGroupId
      || (typeof institutionGroupId === 'object' && institutionGroupId._id === platformGroupId));
  // Set by the server for lecturers and admins; undefined for trainees.
  const readOnly = scenario.canEdit === false;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={() => onSelect(_id)} sx={{ flex: 1 }}>
        <CardContent>
          {/* Header: name + difficulty + visibility chip */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, mr: 1 }}>
              {name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              {isBlind && (
                <Chip
                  icon={<VisibilityOffIcon />}
                  label="Blind Assessment"
                  size="small"
                  color="secondary"
                />
              )}
              {isReferralOnly && (
                <Chip
                  icon={<DescriptionIcon />}
                  label="Referral Mode"
                  size="small"
                  color="info"
                />
              )}
              <Chip
                label={difficulty}
                size="small"
                color={DIFFICULTY_COLORS[difficulty] || 'default'}
                sx={{ textTransform: 'capitalize' }}
              />
              {!readOnly && scenario.studentAccess && scenario.studentAccess !== 'library' && (
                <Chip
                  label={scenario.studentAccess === 'assignment_only' ? 'Assignment Only' : 'Hidden'}
                  size="small"
                  variant="outlined"
                  color={scenario.studentAccess === 'assignment_only' ? 'warning' : 'default'}
                />
              )}
              {scenario.sharedWithOrgs && scenario.sharedWithOrgs.length > 0 && (
                <Chip label="Shared" size="small" variant="outlined" color="info" />
              )}
              {isPlatformLibrary && (
                <Chip label="Platform Library" size="small" variant="outlined" color="primary" />
              )}
            </Box>
          </Box>

          {/* Description — full_brief only */}
          {!isBlind && !isReferralOnly && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {description}
            </Typography>
          )}

          {/* Referral summary — referral_only mode */}
          {isReferralOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
              {referralSummary || 'Referral available'}
            </Typography>
          )}

          {/* Setting + category + time limit */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {/* Category: shown for full_brief and referral_only, hidden for blind */}
            {!isBlind && category && (
              <Chip
                label={CATEGORY_LABELS[category] || category}
                size="small"
                variant="outlined"
              />
            )}
            {scenarioContext?.timeLimit && (
              <Chip
                icon={<AccessTimeIcon />}
                label={`${scenarioContext.timeLimit} min`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {/* Footer: objectives + assessments — full_brief only */}
          {!isBlind && !isReferralOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {learningObjectives.length} objective{learningObjectives.length !== 1 ? 's' : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {availableAssessments.slice(0, 3).map(a => (
                  <Chip key={a} label={a.toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
      {readOnly && onCopy && (
        <CardActions sx={{ px: 2, pt: 0, pb: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={(e) => { e.stopPropagation(); onCopy(_id); }}
          >
            Use in my group
          </Button>
        </CardActions>
      )}
      {!readOnly && (onEdit || onToggleAccess) && (
        <CardActions sx={{ px: 2, pt: 0, pb: 1, justifyContent: 'space-between' }}>
          {onToggleAccess && scenario.studentAccess !== 'assignment_only' ? (
            <FormControlLabel
              onClick={(e) => e.stopPropagation()}
              control={(
                <Switch
                  size="small"
                  checked={scenario.studentAccess === 'library'}
                  onChange={(e) => onToggleAccess(_id, e.target.checked ? 'library' : 'hidden')}
                />
              )}
              label={<Typography variant="caption">Available to trainees</Typography>}
            />
          ) : <span />}
          {onEdit && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={(e) => { e.stopPropagation(); onEdit(_id); }}
            >
              Edit
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default ScenarioCard;
